import React, { useState, useEffect } from "react";
import {
  get_store_requests,
  get_store_request_details,
  update_store_request,
  save_admin_notes,
  get_secure_document_url,
} from "../reduxData/user/userAction";
import {
  Table,
  Button,
  Tabs,
  Tab,
  Spinner,
  Alert,
  Modal,
  Form,
} from "react-bootstrap";
import { Link } from "react-router-dom";

const StoreManager = () => {
  const [requests, setRequests] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  useEffect(() => {
    const fetchRequests = async () => {
      setIsLoading(true);
      setError(null);
      const data = await get_store_requests(activeTab);
      if (data) {
        setRequests(data);
      } else {
        setError("Failed to load requests.");
      }
      setIsLoading(false);
    };
    fetchRequests();
  }, [activeTab]);

  const handleViewDetails = async (id) => {
    const details = await get_store_request_details(id);
    if (details) {
      setSelectedRequest(details);
      setAdminNotes(details.adminNotes || "");
      setShowModal(true);
    } else {
      alert("Could not fetch request details.");
    }
  };

  const handleUpdateRequest = async (status) => {
    if (!selectedRequest) return;

    let payload = { status, adminNotes };
    let confirmationMessage = `Are you sure you want to set this request's status to "${status}"?`;

    if (status === "rejected") {
      const reason = window.prompt(
        "Please provide a reason for rejection (optional, will be included in user notification/email)."
      );
      if (reason === null) {
        return;
      }
      payload.rejectionReason = reason;
    }

    // --- Standard Confirmation ---
    if (!window.confirm(confirmationMessage)) {
      return;
    }

    const result = await update_store_request(selectedRequest._id, payload);

    if (result && result.status) {
      alert(`Request successfully ${status}.`);
      setShowModal(false);
      setSelectedRequest(null);
      setActiveTab(activeTab);
      const updatedRequests = await get_store_requests(activeTab);
      setRequests(updatedRequests);
    } else {
      alert("Failed to update request.");
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedRequest) return;
    await save_admin_notes(selectedRequest._id, adminNotes);
  };

  const formatKey = (key) => {
    return key
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const handleViewDocument = async (documentKey) => {
    const secureUrl = await get_secure_document_url(documentKey);
    if (secureUrl) {
      window.open(secureUrl, "_blank");
    }
  };

  const StoreInfoDisplay = ({ storeInfo, onViewDocument }) => {
    if (!storeInfo) return <p>No store information submitted.</p>;

    return (
      <div>
        {Object.entries(storeInfo).map(([key, value]) => {
          if (!value || value.length === 0 || key === "privacy") {
            return null;
          }

          if (key === "legal_doc" && Array.isArray(value)) {
            return (
              <div className="row detail-row" key={key}>
                <div className="col-sm-4">
                  <strong>{formatKey(key)}:</strong>
                </div>
                <div className="col-sm-8">
                  {value.map((docUrl, index) => {
                    const documentKey = docUrl.substring(
                      docUrl.indexOf("stores/documents/")
                    );
                    return (
                      <div key={index} style={{ textDecoration: "underline" }}>
                        <Link
                          to={documentKey}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {documentKey.split("/").pop()}
                        </Link>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }

          return (
            <div className="row detail-row" key={key}>
              <div className="col-sm-4">
                <strong>{formatKey(key)}:</strong>
              </div>
              <div className="col-sm-8">{value.toString()}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading)
      return (
        <div className="text-center">
          <Spinner animation="border" />
        </div>
      );
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (requests.length === 0)
      return <Alert variant="info">No {activeTab} requests found.</Alert>;

    return (
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>User Name</th>
            <th>User Email</th>
            <th>Date Submitted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((req, index) => (
            <tr key={req._id}>
              <td>{index + 1}</td>
              <td>{req.user?.name || "N/A"}</td>
              <td>{req.user?.email || "N/A"}</td>
              <td>{new Date(req.createdAt).toLocaleDateString()}</td>
              <td>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleViewDetails(req._id)}
                >
                  View Details
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <div className="container">
      <h2>Store Requests Management</h2>
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        <Tab eventKey="pending" title="Pending" />
        <Tab eventKey="approved" title="Approved" />
        <Tab eventKey="rejected" title="Rejected" />
        <Tab eventKey="further action required" title="Unverified" />
      </Tabs>
      {renderContent()}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Request Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRequest ? (
            <div>
              <h5>User Info</h5>
              <p>
                <strong>Name:</strong> {selectedRequest.user.name}
              </p>
              <p>
                <strong>Email:</strong> {selectedRequest.user.email}
              </p>
              <hr />
              <h5>Submitted Store Info</h5>

              <StoreInfoDisplay
                storeInfo={selectedRequest.user.store_info}
                onViewDocument={handleViewDocument}
              />

              <hr />
              <h5>Admin Notes (Internal Use Only)</h5>
              <Form.Control
                as="textarea"
                rows={3}
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                placeholder="Add notes for your team..."
              />
            </div>
          ) : (
            <Spinner animation="border" />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="info" onClick={handleSaveNotes} className="me-auto">
            Save Notes
          </Button>
          <Button
            variant="warning"
            onClick={() => handleUpdateRequest("further action required")}
          >
            Don't Verifiy
          </Button>
          <Button
            variant="danger"
            onClick={() => handleUpdateRequest("rejected")}
          >
            Reject
          </Button>
          <Button
            variant="success"
            onClick={() => handleUpdateRequest("approved")}
          >
            Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StoreManager;
